import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { Button, Dialog, Divider, Grid, Stack, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import AuthorizedBox from "@entrilia/shared/components/AuthorizedBox";
import CloseIconButton from "@entrilia/shared/components/CloseIconButton";
import ScrollableFlexContainer from "@entrilia/shared/components/ScrollableFlexContainer";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import biApi from "../../../../../../../../api/biApi";
import type { MeasureDescriptor } from "../../../../../../../../api/types/svTypes.ts";
import LockIcon from "@entrilia/shared/icons/LockIcon.tsx";
import { createMetaDataAction } from "../../../../store/utils/storeUtils";
import FormulaContainer from "./FormulaContainer";
import { useCustomMeasure } from "./hooks/useCustomMeasure";
import Properties from "./properties/Properties";

const lockCustomMeasure = withErrorHandling(biApi.lockCustomMeasure);
const unlockCustomMeasure = withErrorHandling(biApi.unlockCustomMeasure);

interface Props {
  measure: MeasureDescriptor;
  onClose: () => void;
  onEdit?: (measure: MeasureDescriptor) => void;
  readonly?: boolean;
}

const ExplainMeasureDialog = ({ measure, onClose, onEdit, readonly }: Props) => {
  const dispatch = useDispatch();
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const state = useCustomMeasure({ measure });

  const [currentMeasure, setCurrentMeasure] = useState<MeasureDescriptor>(measure);
  const [saving, setSaving] = useState(false);
  const isLocked = currentMeasure.isLocked === true;

  const handleToggleLock = useCallback(async () => {
    setSaving(true);
    try {
      const id = currentMeasure.measureId ?? currentMeasure.name;
      const [data, error] = isLocked ? await unlockCustomMeasure(id) : await lockCustomMeasure(id);
      if (error || !data) {
        if (error) {
          logError(error, "[ExplainMeasureDialog] toggleLock");
        }
        sendNotificationError(
          isLocked
            ? "An error occurred while unlocking the measure for editing."
            : "An error occurred while locking the measure for editing."
        );
      } else {
        setCurrentMeasure(data);
        dispatch(createMetaDataAction("updateMeasure", data));
        sendNotification(
          `The measure "${currentMeasure.caption}" has been ${isLocked ? "unlocked" : "locked"} for editing.`
        );
      }
    } finally {
      setSaving(false);
    }
  }, [currentMeasure, isLocked, dispatch, sendNotification, sendNotificationError]);

  return (
    <Dialog
      open={true}
      slotProps={{
        paper: {
          sx: {
            width: "100%",
            height: "100%",
            borderRadius: "4px",
            maxWidth: "1000px",
            maxHeight: "700px",
          },
        },
      }}
      onClose={onClose}
    >
      <Grid container sx={{ alignItems: "center", px: 3, py: 2, justifyContent: "space-between", width: "100%" }}>
        <Typography variant="h6">
          {state.caption}{" "}
          {!state.isConfigurable && (
            <Typography component={"span"} color="textSecondary">
              (System measure)
            </Typography>
          )}
        </Typography>
        <CloseIconButton onClick={onClose} />
      </Grid>
      <Divider />
      <FormulaContainer
        state={state}
        measure={measure}
        editable={false}
        showCalculatedBy
        sx={{ pt: 2, pb: 2, px: 3 }}
      />
      <Divider />
      <ScrollableFlexContainer>
        <Properties editable={false} state={state} />
      </ScrollableFlexContainer>
      <Divider />
      <Grid
        container
        sx={{ justifyContent: "space-between", alignItems: "center", py: 2, px: 3, gap: 1, width: "100%" }}
      >
        <Stack direction="row" sx={{ gap: 1, alignItems: "center", flexGrow: 1 }}>
          {currentMeasure.custom && !readonly && (
            <AuthorizedBox permissions={["ManageCustomMeasures"]}>
              <Stack direction="row" sx={{ gap: 1, alignItems: "center" }}>
                {!isLocked && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => onEdit?.(currentMeasure)}
                    startIcon={<EditOutlinedIcon />}
                  >
                    Edit Measure
                  </Button>
                )}
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleToggleLock}
                  loading={saving}
                  startIcon={isLocked ? <LockOpenOutlinedIcon /> : <LockOutlinedIcon />}
                >
                  {isLocked ? "Unlock Editing" : "Lock Editing"}
                </Button>
              </Stack>
            </AuthorizedBox>
          )}
          {isLocked && currentMeasure.modifiedBy && (
            <Stack direction="row" sx={{ gap: 0.5, alignItems: "center" }}>
              <LockIcon color="primary" />
              <Typography variant="caption" color="textSecondary">
                Locked by
              </Typography>
              <Typography component="span">{currentMeasure.modifiedBy}</Typography>
            </Stack>
          )}
        </Stack>
        <Button variant="contained" onClick={onClose}>
          Close
        </Button>
      </Grid>
    </Dialog>
  );
};

export default ExplainMeasureDialog;
